import { useState } from "react";
import { api, setToken, type User } from "../api.js";
import { Mark } from "../Mark.js";
import { Button, Field, Msg } from "../ui.js";

/**
 * Sign in. There is no sign up.
 *
 * Orbit runs one club. Accounts are made by the owner, not by strangers, so the
 * screen offers the two ways an existing member gets in — password, or a code
 * to their email — and nothing else. The club-creation form that used to live
 * here is gone, along with the route behind it.
 */

type Mode = "signin" | "code";

export function Auth({ onSignedIn }: { onSignedIn: (u: User) => void }) {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setMsg(null);
    try { await fn(); } finally { setBusy(false); }
  };

  const signIn = () => run(async () => {
    const r = await api<{ user: User; token?: string; error?: string }>("/api/v1/auth/login", { json: { email, password } });
    // Native has no usable cookie, so the session token travels as a
    // bearer header instead. In a browser this is null and ignored.
    if (r.ok && r.data.token) setToken(r.data.token);
    if (r.ok && r.data.user) onSignedIn(r.data.user);
    else setMsg(r.data.error ?? "Could not sign in.");
  });

  const askCode = () => run(async () => {
    const r = await api<{ error?: string }>("/api/v1/auth/otp/request", { json: { email } });
    if (r.ok) { setMode("code"); setMsg(null); }
    else setMsg(r.data.error ?? "Could not send a code.");
  });

  const verify = () => run(async () => {
    const r = await api<{ user: User; token?: string; error?: string }>("/api/v1/auth/otp/verify", { json: { email, code } });
    if (r.ok && r.data.token) setToken(r.data.token);
    if (r.ok && r.data.user) onSignedIn(r.data.user);
    else setMsg(r.data.error ?? "That code is not right.");
  });

  return (
    <div className="mx-auto flex min-h-full w-full max-w-[400px] flex-col justify-center px-4 py-8">
      <div className="mb-7 text-center">
        <div className="mb-3.5 flex justify-center"><Mark size={92} /></div>
        <h1 className="font-num text-hero font-semibold tracking-[.16em]">ORBIT</h1>
        <p className="mt-2 text-body text-faint">Ball-by-ball, for your club.</p>
      </div>

      <Msg text={msg} kind="err" />

      {mode === "code" ? (
        <div className="space-y-4">
          <p className="text-center text-body text-faint">
            If {email} has an account, a code is on its way.
          </p>
          <Field label="Six-digit code" value={code} inputMode="numeric" maxLength={6}
                 onChange={(e) => setCode(e.target.value)} />
          <Button full onClick={verify} disabled={busy}>{busy ? "Checking…" : "Verify"}</Button>
          <Button full kind="ghost" onClick={() => { setMode("signin"); setMsg(null); }}>Back</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label="Email" type="email" autoComplete="email" placeholder="you@club.org"
                 value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field label="Password" type="password" value={password}
                 autoComplete="current-password"
                 onChange={(e) => setPassword(e.target.value)} />
          <Button full onClick={signIn} disabled={busy}>
            {busy ? "Working…" : "Sign in"}
          </Button>

          <div className="flex items-center gap-3 py-1 font-mono text-label tracking-[.16em] text-faint">
            <span className="h-px flex-1 bg-line" />OR<span className="h-px flex-1 bg-line" />
          </div>

          <Button full kind="ghost" onClick={askCode} disabled={busy || !email}>
            Email me a code instead
          </Button>

          {/* Deliberately not a link. There is nowhere to sign up, and pretending
              otherwise wastes the time of whoever taps it. */}
          <p className="pt-1 text-center text-caption leading-relaxed text-faint">
            Orbit is invite-only. Ask the club to add you.
          </p>
        </div>
      )}
    </div>
  );
}
